import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

// Global Processing Context
const ProcessingContext = createContext({
  isProcessing: false,
  processingCount: 0,
  startProcessing: () => {},
  stopProcessing: () => {},
  setProcessing: () => {},
});

export const useProcessing = () => useContext(ProcessingContext);

export const ProcessingProvider = ({ children }) => {
  const [processingCount, setProcessingCount] = useState(0);
  const timeoutRef = useRef(null);

  // Derived state - is any processing happening
  const isProcessing = processingCount > 0;

  // Start a processing task (increments counter)
  const startProcessing = useCallback(() => {
    setProcessingCount(prev => prev + 1);
  }, []);

  // Stop a processing task (decrements counter with safety)
  const stopProcessing = useCallback(() => {
    setProcessingCount(prev => Math.max(0, prev - 1));
  }, []);

  // Direct set (for manual control)
  const setProcessing = useCallback((value) => {
    if (value) {
      startProcessing();
    } else {
      // Delayed stop to ensure smooth animation completion
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setProcessingCount(0);
      }, 300);
    }
  }, [startProcessing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <ProcessingContext.Provider value={{ 
      isProcessing, 
      processingCount,
      startProcessing, 
      stopProcessing,
      setProcessing 
    }}>
      {children}
    </ProcessingContext.Provider>
  );
};

// Hook to wrap async operations with processing state
export const useProcessingAsync = () => {
  const { startProcessing, stopProcessing } = useProcessing();

  const withProcessing = useCallback(async (asyncFn) => {
    startProcessing();
    try {
      return await asyncFn();
    } finally {
      stopProcessing();
    }
  }, [startProcessing, stopProcessing]);

  return { withProcessing };
};

export default ProcessingContext;
